/**
 * The client's objects (`GET /sites`) and one object (`GET /sites/:id`,
 * owner or administrator); connecting, «Налаштування» and disconnecting
 * (iteration 2, C4 routes); the access of Phage.Energy to the station (D3/D4:
 * data and control go through it — the client's DeyeCloud password only
 * lists the stations and is not kept).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InputCt, PhageAccessInfoResponse, SiteResponse, SiteSettingsBody, TariffType } from 'shared';

import { api } from '../lib/api-client.js';

const base = (siteId: string) => `/api/sites/${encodeURIComponent(siteId)}`;

export function useSites(enabled = true) {
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => api.get<{ sites: SiteResponse[] }>('/api/sites').then((r) => r.sites),
    enabled,
  });
}

export const fetchSite = (siteId: string) => api.get<SiteResponse>(base(siteId));

export function useSite(siteId: string) {
  return useQuery({
    queryKey: ['site', siteId],
    queryFn: () => fetchSite(siteId),
    enabled: !!siteId,
  });
}

export interface BatchSite {
  /** D5: the inverter's SN from its sticker. */
  device_sn: string;
  name: string;
  /** D6: not asked at connection — DeyeCloud fills them once access is open. */
  capacity_kwh?: number;
  p_max_kw?: number;
  input_ct?: InputCt;
  model?: string;
}

export interface BatchBody {
  tariff_type?: TariffType;
  region?: string;
  osr_code?: string;
  sites: BatchSite[];
}

/** `POST /sites/batch` (C4): several inverters of one DeyeCloud listing; they wait for access (D4). */
export function useConnectSites() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BatchBody) =>
      api
        .post<{ sites: SiteResponse[] }>('/api/sites/batch', { vendor: 'deye', ...body })
        .then((r) => r.sites),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export function useUpdateSettings(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SiteSettingsBody) => api.patch<SiteResponse>(`${base(siteId)}/settings`, body),
    onSuccess: (site) => {
      qc.setQueryData(['site', siteId], site);
      void qc.invalidateQueries({ queryKey: ['sites'] });
      void qc.invalidateQueries({ queryKey: ['site-overview', siteId] });
      void qc.invalidateQueries({ queryKey: ['site-potential', siteId] });
      // Tariff, battery and allowed power change «Гроші».
      void qc.invalidateQueries({ queryKey: ['site-money', siteId] });
    },
  });
}

export interface DisconnectResponse {
  ok: true;
  restore: { status: 'sent' | 'skipped' | 'failed'; reason: string | null };
}

/** Original settings back first; 502 `restore_failed` → offer `force`. */
export function useDisconnectSite(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (force: boolean) =>
      api.del<DisconnectResponse>(`${base(siteId)}${force ? '?force=true' : ''}`),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['site', siteId] });
      void qc.invalidateQueries({ queryKey: ['sites'] });
      void qc.invalidateQueries({ queryKey: ['admin-sites'] });
    },
  });
}

/**
 * «Оновити з інвертора» (D2): read the inverter's settings now — at most once
 * in 10 minutes (429 `too_soon`); the stored object comes back.
 */
export function useRefreshInverterSettings(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SiteResponse>(`${base(siteId)}/inverter-settings/refresh`),
    onSuccess: (site) => {
      qc.setQueryData(['site', siteId], site);
      void qc.invalidateQueries({ queryKey: ['sites'] });
    },
    // A read without an answer still counts for the 10 minutes: the stored attempt comes back.
    onError: () => void qc.invalidateQueries({ queryKey: ['site', siteId] }),
  });
}

/**
 * «Повернути програму Phage» (owner, 25.10.2026): the program was changed by
 * hand on the inverter — Phage's goes back (409 `not_changed` — no mark); the
 * stored object comes back, the program is written in the background.
 */
export function useResumeProgram(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SiteResponse>(`${base(siteId)}/program/resume`),
    onSuccess: (site) => {
      qc.setQueryData(['site', siteId], site);
      void qc.invalidateQueries({ queryKey: ['sites'] });
      void qc.invalidateQueries({ queryKey: ['admin-sites'] });
    },
    onError: () => void qc.invalidateQueries({ queryKey: ['site', siteId] }),
  });
}

/** D3: the business name of Phage.Energy for the instruction, whether a check can work. */
export function usePhageAccessInfo() {
  return useQuery({
    queryKey: ['phage-access'],
    queryFn: () => api.get<PhageAccessInfoResponse>('/api/phage-access'),
    staleTime: 5 * 60_000,
  });
}

/**
 * «Перевірити доступ» (D3): the station in the Phage.Energy account — at most
 * once a minute (429 `too_soon`); the stored object comes back.
 */
export function useCheckPhageAccess(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SiteResponse>(`${base(siteId)}/phage-access/check`),
    onSuccess: (site) => {
      qc.setQueryData(['site', siteId], site);
      void qc.invalidateQueries({ queryKey: ['sites'] });
      void qc.invalidateQueries({ queryKey: ['admin-sites'] });
      // D4: an access just opened brings data, settings and the history download.
      void qc.invalidateQueries({ queryKey: ['site-overview', siteId] });
      void qc.invalidateQueries({ queryKey: ['site-analysis', siteId] });
      // A check may have learnt the business name.
      void qc.invalidateQueries({ queryKey: ['phage-access'] });
    },
    // An attempt without an answer still counts for the minute: the stored attempt comes back.
    onError: () => void qc.invalidateQueries({ queryKey: ['site', siteId] }),
  });
}
